import {
  isFirestoreSafeOrganizationContentId,
  isFirestoreSafeOrganizationId,
  isOrganizationContentStatus,
  isOrganizationContentStoragePath,
  isOrganizationContentTypeId,
  normalizeYouTubeVideoUrl,
  normalizeOrganizationContentId,
  type OrganizationContentTypeId,
  type OrganizationContentMediaReference,
  type OrganizationContentStatus,
  type OrganizationContentYoutubeReference,
} from "../../../packages/foundation/src/index.js";

export type ImamContentEditorInput = {
  contentId: string;
  contentType: OrganizationContentTypeId;
  status: OrganizationContentStatus;
  title: string;
  titleAr: string;
  subtitle: string;
  subtitleAr: string;
  body: string;
  bodyAr: string;
  imageStoragePath: string;
  imageAltText: string;
  imageAltTextAr: string;
  galleryImageStoragePath1: string;
  galleryImageAltText1: string;
  galleryImageAltTextAr1: string;
  galleryImageStoragePath2: string;
  galleryImageAltText2: string;
  galleryImageAltTextAr2: string;
  galleryImageStoragePath3: string;
  galleryImageAltText3: string;
  galleryImageAltTextAr3: string;
  youtubeUrl: string;
  sortOrder: string;
  featured: boolean;
};

export type ImamContentEditorDraft = {
  contentId: string;
  type: OrganizationContentTypeId;
  status: OrganizationContentStatus;
  visibility: "connected";
  title: string;
  titleAr: string | null;
  subtitle: string | null;
  subtitleAr: string | null;
  body: string;
  bodyAr: string | null;
  image: {
    storagePath: string | null;
    altText: string | null;
    altTextAr: string | null;
  };
  galleryImage1: OrganizationContentMediaReference | null;
  galleryImage2: OrganizationContentMediaReference | null;
  galleryImage3: OrganizationContentMediaReference | null;
  youtube: OrganizationContentYoutubeReference | null;
  sortOrder: number;
  featured: boolean;
  source: "experience_twenty_two" | "ramadan_mobile_v1";
};

export type ImamContentEditorResult =
  | {
      ok: true;
      draft: ImamContentEditorDraft;
    }
  | {
      ok: false;
      errors: string[];
    };

export type ImamContentEditorContent = {
  contentId: string;
  type: OrganizationContentTypeId | string;
  status: OrganizationContentStatus | string;
  title: string;
  titleAr: string | null;
  subtitle: string | null;
  subtitleAr: string | null;
  body: string;
  bodyAr: string | null;
  image: {
    storagePath: string | null;
    altText: string | null;
    altTextAr: string | null;
  };
  galleryImage1?: OrganizationContentMediaReference | null;
  galleryImage2?: OrganizationContentMediaReference | null;
  galleryImage3?: OrganizationContentMediaReference | null;
  youtube?: OrganizationContentYoutubeReference | null;
  sortOrder: number;
  featured: boolean;
};

const titleLimit = 140;
const subtitleLimit = 180;
const bodyLimit = 2400;
const altTextLimit = 160;

export function buildImamContentEditorInputFromContent(
  content: ImamContentEditorContent,
): ImamContentEditorInput {
  return {
    body: content.body,
    bodyAr: content.bodyAr ?? "",
    contentId: content.contentId,
    contentType: isOrganizationContentTypeId(content.type) ? content.type : "imam_profile",
    featured: content.featured,
    galleryImageAltText1: content.galleryImage1?.altText ?? "",
    galleryImageAltText2: content.galleryImage2?.altText ?? "",
    galleryImageAltText3: content.galleryImage3?.altText ?? "",
    galleryImageAltTextAr1: content.galleryImage1?.altTextAr ?? "",
    galleryImageAltTextAr2: content.galleryImage2?.altTextAr ?? "",
    galleryImageAltTextAr3: content.galleryImage3?.altTextAr ?? "",
    galleryImageStoragePath1: content.galleryImage1?.storagePath ?? "",
    galleryImageStoragePath2: content.galleryImage2?.storagePath ?? "",
    galleryImageStoragePath3: content.galleryImage3?.storagePath ?? "",
    imageAltText: content.image.altText ?? "",
    imageAltTextAr: content.image.altTextAr ?? "",
    imageStoragePath: content.image.storagePath ?? "",
    sortOrder: String(content.sortOrder),
    status: isOrganizationContentStatus(content.status) ? content.status : "draft",
    subtitle: content.subtitle ?? "",
    subtitleAr: content.subtitleAr ?? "",
    title: content.title,
    titleAr: content.titleAr ?? "",
    youtubeUrl: content.youtube?.canonicalUrl ?? "",
  };
}

export function buildDefaultImamContentEditorInput(organizationId: string): ImamContentEditorInput {
  return {
    body: "Introduce the Imam, teaching programs, spiritual guidance, and community care services for connected people.",
    bodyAr:
      "عرّف بالإمام وبرامج التعليم والإرشاد الروحي وخدمات الرعاية المجتمعية للأشخاص المتصلين.",
    contentId: "imam-profile",
    contentType: "imam_profile",
    featured: true,
    galleryImageAltText1: "",
    galleryImageAltText2: "",
    galleryImageAltText3: "",
    galleryImageAltTextAr1: "",
    galleryImageAltTextAr2: "",
    galleryImageAltTextAr3: "",
    galleryImageStoragePath1: "",
    galleryImageStoragePath2: "",
    galleryImageStoragePath3: "",
    imageAltText: "",
    imageAltTextAr: "",
    imageStoragePath: `organizations/${organizationId}/content/imam-profile/imam-profile.jpg`,
    sortOrder: "10",
    status: "draft",
    subtitle: "Guidance, learning, and community care",
    subtitleAr: "إرشاد وتعليم ورعاية مجتمعية",
    title: "Imam Profile",
    titleAr: "ملف الإمام",
    youtubeUrl: "",
  };
}

export function buildEmptyImamContentEditorInput(): ImamContentEditorInput {
  return {
    body: "",
    bodyAr: "",
    contentId: "",
    contentType: "imam_profile",
    featured: false,
    galleryImageAltText1: "",
    galleryImageAltText2: "",
    galleryImageAltText3: "",
    galleryImageAltTextAr1: "",
    galleryImageAltTextAr2: "",
    galleryImageAltTextAr3: "",
    galleryImageStoragePath1: "",
    galleryImageStoragePath2: "",
    galleryImageStoragePath3: "",
    imageAltText: "",
    imageAltTextAr: "",
    imageStoragePath: "",
    sortOrder: "10",
    status: "draft",
    subtitle: "",
    subtitleAr: "",
    title: "",
    titleAr: "",
    youtubeUrl: "",
  };
}

export function evaluateImamContentEditorInput(
  organizationId: string,
  input: ImamContentEditorInput,
): ImamContentEditorResult {
  const contentId = normalizeOrganizationContentId(input.contentId);
  const title = input.title.trim();
  const titleAr = nullableTrimmedText(input.titleAr);
  const subtitle = nullableTrimmedText(input.subtitle);
  const subtitleAr = nullableTrimmedText(input.subtitleAr);
  const body = input.body.trim();
  const bodyAr = nullableTrimmedText(input.bodyAr);
  const imageStoragePath = nullableTrimmedText(input.imageStoragePath);
  const imageAltText = mediaAltTextForStoragePath(
    imageStoragePath,
    input.imageAltText,
    `${title} image`,
  );
  const imageAltTextAr = mediaAltTextForStoragePath(
    imageStoragePath,
    input.imageAltTextAr,
    titleAr ? `صورة ${titleAr}` : `${title} image`,
  );
  const errors: string[] = [];
  const galleryImageStoragePath1 = nullableTrimmedText(input.galleryImageStoragePath1);
  const galleryImageStoragePath2 = nullableTrimmedText(input.galleryImageStoragePath2);
  const galleryImageStoragePath3 = nullableTrimmedText(input.galleryImageStoragePath3);
  const galleryImage1 = evaluateMediaReferenceInput(
    organizationId,
    contentId,
    "Gallery image 1",
    galleryImageStoragePath1,
    mediaAltTextForStoragePath(
      galleryImageStoragePath1,
      input.galleryImageAltText1,
      `${title} gallery image 1`,
    ),
    mediaAltTextForStoragePath(
      galleryImageStoragePath1,
      input.galleryImageAltTextAr1,
      titleAr ? `صورة ${titleAr} 1` : `${title} gallery image 1`,
    ),
    errors,
  );
  const galleryImage2 = evaluateMediaReferenceInput(
    organizationId,
    contentId,
    "Gallery image 2",
    galleryImageStoragePath2,
    mediaAltTextForStoragePath(
      galleryImageStoragePath2,
      input.galleryImageAltText2,
      `${title} gallery image 2`,
    ),
    mediaAltTextForStoragePath(
      galleryImageStoragePath2,
      input.galleryImageAltTextAr2,
      titleAr ? `صورة ${titleAr} 2` : `${title} gallery image 2`,
    ),
    errors,
  );
  const galleryImage3 = evaluateMediaReferenceInput(
    organizationId,
    contentId,
    "Gallery image 3",
    galleryImageStoragePath3,
    mediaAltTextForStoragePath(
      galleryImageStoragePath3,
      input.galleryImageAltText3,
      `${title} gallery image 3`,
    ),
    mediaAltTextForStoragePath(
      galleryImageStoragePath3,
      input.galleryImageAltTextAr3,
      titleAr ? `صورة ${titleAr} 3` : `${title} gallery image 3`,
    ),
    errors,
  );
  const youtubeUrl = nullableTrimmedText(input.youtubeUrl);
  const youtube = youtubeUrl === null ? null : normalizeYouTubeVideoUrl(youtubeUrl);
  const sortOrder = Number.parseInt(input.sortOrder, 10);

  if (!isFirestoreSafeOrganizationId(organizationId)) {
    errors.push("Organization ID must be Firestore-safe.");
  }

  if (!contentId || !isFirestoreSafeOrganizationContentId(contentId)) {
    errors.push("Content ID must be Firestore-safe.");
  }

  if (!isOrganizationContentStatus(input.status)) {
    errors.push("Status must be draft, published, or archived.");
  }

  if (!isOrganizationContentTypeId(input.contentType)) {
    errors.push("Content type must be leadership profile or Ramadan message.");
  }

  validateRequiredText("English title", title, titleLimit, errors);
  validateOptionalText("Arabic title", titleAr, titleLimit, errors);
  validateOptionalText("English subtitle", subtitle, subtitleLimit, errors);
  validateOptionalText("Arabic subtitle", subtitleAr, subtitleLimit, errors);
  validateRequiredText("English body", body, bodyLimit, errors);
  validateOptionalText("Arabic body", bodyAr, bodyLimit, errors);
  validateOptionalText("Image alt text", imageAltText, altTextLimit, errors);
  validateOptionalText("Arabic image alt text", imageAltTextAr, altTextLimit, errors);

  if (!Number.isInteger(sortOrder) || sortOrder < 0 || sortOrder > 10000) {
    errors.push("Sort order must be an integer from 0 to 10000.");
  }

  if (
    imageStoragePath !== null &&
    contentId &&
    !isOrganizationContentStoragePath(organizationId, contentId, imageStoragePath)
  ) {
    errors.push("Image path must stay under the organization content folder.");
  }

  if (youtubeUrl !== null && youtube === null) {
    errors.push("YouTube URL must be a valid YouTube watch, short, shorts, embed, or iframe URL.");
  }

  if (errors.length > 0) {
    return { errors, ok: false };
  }

  return {
    draft: {
      body,
      bodyAr,
      contentId,
      featured: input.featured,
      image: {
        altText: imageAltText,
        altTextAr: imageAltTextAr,
        storagePath: imageStoragePath,
      },
      galleryImage1,
      galleryImage2,
      galleryImage3,
      sortOrder,
      source:
        input.contentType === "ramadan_message" ? "ramadan_mobile_v1" : "experience_twenty_two",
      status: input.status,
      subtitle,
      subtitleAr,
      title,
      titleAr,
      type: input.contentType,
      visibility: "connected",
      youtube,
    },
    ok: true,
  };
}

function evaluateMediaReferenceInput(
  organizationId: string,
  contentId: string,
  label: string,
  storagePath: string | null,
  altText: string | null,
  altTextAr: string | null,
  errors: string[],
): OrganizationContentMediaReference | null {
  validateOptionalText(`${label} alt text`, altText, altTextLimit, errors);
  validateOptionalText(`${label} Arabic alt text`, altTextAr, altTextLimit, errors);

  if (storagePath === null && altText === null && altTextAr === null) {
    return null;
  }

  if (storagePath === null) {
    errors.push(`${label} needs an image path when alt text is set.`);
  } else if (
    contentId &&
    !isOrganizationContentStoragePath(organizationId, contentId, storagePath)
  ) {
    errors.push(`${label} path must stay under the organization content folder.`);
  }

  return {
    altText,
    altTextAr,
    storagePath,
  };
}

function nullableTrimmedText(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

function mediaAltTextForStoragePath(
  storagePath: string | null,
  providedAltText: string,
  fallbackAltText: string,
): string | null {
  if (storagePath === null) {
    return null;
  }

  return nullableTrimmedText(providedAltText) ?? nullableTrimmedText(fallbackAltText);
}

function validateRequiredText(
  label: string,
  value: string,
  maxLength: number,
  errors: string[],
): void {
  if (!value) {
    errors.push(`${label} is required.`);
    return;
  }

  if (value.length > maxLength) {
    errors.push(`${label} must be ${maxLength} characters or fewer.`);
  }
}

function validateOptionalText(
  label: string,
  value: string | null,
  maxLength: number,
  errors: string[],
): void {
  if (value === null) {
    return;
  }

  if (value.length > maxLength) {
    errors.push(`${label} must be ${maxLength} characters or fewer.`);
  }
}
